// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pictures and files of an agent prompt referred to where the prompt is written, as T3's
// composer does: a picture pasted mid-sentence shows there as a chip. The text holds it as the
// Markdown link `[label](attached:<name>)`, the name being one of the prompt's own files (names are
// unique within a prompt: AgentComposerFiles), so drafts, the outbox and the sent message keep it.
// The bridge sends it to T3 as T3's own image or file record (konvo-agents 0.59.0+); older bridges
// get the label. Konvo's Markdown only links web addresses, so a sent prompt shows them through
// `https://attached.konvo.invalid/<name>` (`.invalid` never resolves), which AgentPromptMarkdown
// draws as the chip.

const PREFIX = 'attached:';
const DISPLAY = 'https://attached.konvo.invalid/';
const LINK = /!?\[([^\]\n]{0,255})\]\(attached:([^)\s]{1,800})\)/g;

/** A file name as the link holds it: URL-encoded, brackets too (they would end the link). */
function encodeName(name: string): string {
	return encodeURIComponent(name).replace(/[()]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

function decodeName(encoded: string): string {
	try {
		return decodeURIComponent(encoded);
	} catch {
		return encoded;
	}
}

export function attachedUrl(name: string): string {
	return `${PREFIX}${encodeName(name)}`;
}

export function isAttachedUrl(url: string): boolean {
	return url.startsWith(PREFIX) || url.startsWith(DISPLAY);
}

/** The file a link (in the prompt, or as shown) points at, or null. */
export function attachedName(url: string): string | null {
	const encoded = url.startsWith(PREFIX)
		? url.slice(PREFIX.length)
		: url.startsWith(DISPLAY)
			? url.slice(DISPLAY.length)
			: null;
	return encoded ? decodeName(encoded) : null;
}

/** The names of the files a prompt refers to. */
export function attachedNames(prompt: string): Set<string> {
	const names = new Set<string>();
	if (!prompt.includes('](attached:')) return names;
	for (const match of prompt.matchAll(LINK)) names.add(decodeName(match[2]!));
	return names;
}

/** Whether the prompt has words besides its links to files (T3 puts a pasted picture's chip only in text). */
export function promptHasWords(prompt: string): boolean {
	return prompt.replace(LINK, '').trim() !== '';
}

/** The prompt without its links to `name` (that file was taken off), and the space before (or after) each. */
export function withoutAttached(prompt: string, name: string): string {
	if (!prompt.includes('](attached:')) return prompt;
	let out = '';
	let at = 0;
	for (const match of prompt.matchAll(LINK)) {
		if (decodeName(match[2]!) !== name) continue;
		let start = match.index!;
		let end = start + match[0].length;
		if (start > at && prompt[start - 1] === ' ') start -= 1;
		else if (prompt[end] === ' ') end += 1;
		out += prompt.slice(at, start);
		at = end;
	}
	return out + prompt.slice(at);
}

/** A sent prompt for Konvo's Markdown: each link to a file as a web address it links. */
export function attachedDisplay(prompt: string): string {
	if (!prompt.includes('](attached:')) return prompt;
	return prompt.replace(
		LINK,
		(_whole, label: string, encoded: string) => `[${label || decodeName(encoded)}](${DISPLAY}${encoded})`,
	);
}

/** The prompt as plain words: each link to a file as its label (copying a sent prompt). */
export function attachedPlain(prompt: string): string {
	if (!prompt.includes('](attached:')) return prompt;
	return prompt.replace(LINK, (_whole, label: string, encoded: string) => label || decodeName(encoded));
}

/** `name`, or the first of `name-2`, `name-3`… none of `taken` has (the extension kept). */
export function uniqueFileName(name: string, taken: ReadonlySet<string>): string {
	if (!taken.has(name)) return name;
	const dot = name.lastIndexOf('.');
	const [base, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
	for (let n = 2; ; n++) {
		const next = `${base}-${n}${ext}`;
		if (!taken.has(next)) return next;
	}
}
